import moment from 'moment-timezone';
import { GanttChartMode } from 'sb/shared/constants';

/**
 * Persistence for every Smart Calendar toolbar selection (VE-2259).
 *
 * Before this module only Group View survived a visit, under a bare global `renewalGroupView`
 * key; Category, Client Contact, Expires In, Status and Renewal Zone were rebuilt from their
 * defaults each time the user came back. All six now live in ONE object under ONE key, because
 * the AC requires them to be "restored together": had Group View stayed in its own unscoped key,
 * two users sharing a browser would get one user's Group View next to the other's default filters.
 *
 * Two properties of the design are load-bearing:
 *
 * 1. The stored object is REPLACED on every change, never merged. That is what makes a cleared
 *    filter stay cleared — there is no "field absent, fall back to the last saved value" path for
 *    it to revert through.
 * 2. Expires In stores the PRESET (`one_year` / `full`), not the resolved date. A stored absolute
 *    date would silently go stale: "one year out" saved in March would still mean March next year
 *    when reopened in June. `expiresInPresetToDate` re-resolves it against today on every read.
 */

export type GroupView = 'hybrid' | 'flat' | 'vendor';
export type ExpiresInPreset = 'one_year' | 'full';
export type CalendarStatus = 'active' | 'expired';
export type RenewalZone = 'default' | 'orange' | 'red';

/** One entry of the Client Contact filter's option list, as built from the loaded renewals. */
export interface ClientContactOption {
  value: string | number;
  label: string;
}

export interface SmartCalendarPrefs {
  groupView: GroupView;
  /**
   * The antd Cascader selection. Typed `string[]` to match the existing contract on
   * `prepareDataForRender`, though in `multiple` mode the live value is an array of category
   * PATHS (`[['Software', 'Security']]`) — which `getCategoryFilteredTasks` destructures as
   * `[root, prime, second]`. Both shapes round-trip unchanged.
   */
  categories: string[];
  clientContacts: Array<string | number>;
  expiresIn: ExpiresInPreset;
  status: CalendarStatus;
  renewalZone: RenewalZone;
}

const GROUP_VIEWS: readonly GroupView[] = ['hybrid', 'flat', 'vendor'];
const EXPIRES_IN_PRESETS: readonly ExpiresInPreset[] = ['one_year', 'full'];
const CALENDAR_STATUSES: readonly CalendarStatus[] = ['active', 'expired'];
const RENEWAL_ZONES: readonly RenewalZone[] = ['default', 'orange', 'red'];

/** Exactly the selections the toolbar starts with, so a first-time user sees no change. */
export const DEFAULT_SMART_CALENDAR_PREFS: SmartCalendarPrefs = {
  groupView: 'hybrid',
  categories: [],
  clientContacts: [],
  expiresIn: 'one_year',
  status: 'active',
  renewalZone: 'default'
};

const PREFS_KEY_PREFIX = 'smartCalendarPrefs';

/**
 * Builds the storage key for one user's selections on one calendar, or null when there is no user.
 *
 * Scoped by Auth0 subject so filters do not leak between users sharing a browser — Client Contact
 * ids and category names are account-specific. Scoped by mode so the Portfolio Calendar keeps its
 * own Status / Expires In / Renewal Zone.
 *
 * Returns null rather than a placeholder bucket (VE-2605). An earlier revision fell back to a
 * literal `anonymous` segment, and every session that mounted the calendar before Auth0 resolved
 * shared it: one user's selections were read out of it by the next. That window is routine, not
 * exotic — `handleLogout` clears the cached `permissions`, so `ACLCheck` returns false and App's
 * `!isAuthenticated && ACLCheck(...)` gate stops holding, letting the calendar mount with no
 * subject on the first load after any logout.
 * @param {string | undefined} userId - The Auth0 subject (`user.sub`) of the signed-in user.
 * @param {GanttChartMode} mode - Which calendar the selections belong to.
 * @returns {string | null} The storage key, or null while the signed-in user is unknown.
 */
export const smartCalendarPrefsKey = (userId: string | undefined, mode: GanttChartMode): string | null =>
  userId ? `${PREFS_KEY_PREFIX}:${userId}:${mode}` : null;

/**
 * Resolves a stored value against a filter's option set, falling back when it is not one of them.
 *
 * Anything unrecognised is rejected per field rather than discarding the whole object, so one
 * stale or hand-edited value cannot cost the user their other five selections.
 * @param {unknown} raw - The value read back off storage.
 * @param {string[]} allowed - The options the filter can actually take.
 * @param {string} fallback - The value to use when `raw` is not one of `allowed`.
 * @returns {string} A value guaranteed to be selectable in the UI.
 */
const readOption = <T extends string>(raw: unknown, allowed: readonly T[], fallback: T): T =>
  typeof raw === 'string' ? allowed.find((option) => option === raw) ?? fallback : fallback;

/**
 * Recognises one Cascader selection entry: a root category name, or a full category path.
 * @param {unknown} entry - A single element of the stored categories array.
 * @returns {boolean} Whether the entry is a shape the category filter can apply.
 */
const isCategoryEntry = (entry: unknown): boolean =>
  typeof entry === 'string' || (Array.isArray(entry) && entry.every((part) => typeof part === 'string'));

/**
 * Reads the stored category selection, dropping entries the filter could not apply.
 * @param {unknown} raw - The stored `categories` value.
 * @returns {string[]} The surviving selection, empty when nothing usable was stored.
 */
const readCategories = (raw: unknown): string[] => (Array.isArray(raw) ? raw.filter(isCategoryEntry) : []);

/**
 * Reads the stored Client Contact ids, keeping only primitives that can match an option value.
 * @param {unknown} raw - The stored `clientContacts` value.
 * @returns {Array<string | number>} The surviving ids, empty when nothing usable was stored.
 */
const readClientContacts = (raw: unknown): Array<string | number> =>
  Array.isArray(raw) ? raw.filter((entry) => typeof entry === 'string' || typeof entry === 'number') : [];

/**
 * Parses the stored preferences object, treating anything unusable as "nothing stored".
 *
 * Storage can hold corrupt JSON from a hand edit or an interrupted write, and `getItem` itself
 * throws in some privacy modes — neither may break the calendar, so both degrade to defaults.
 * @param {string} storageKey - The key to read.
 * @returns {Record<string, unknown> | null} The parsed object, or null when there is none.
 */
const parseStoredPrefs = (storageKey: string): Record<string, unknown> | null => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch (_e) {
    return null;
  }
};

/**
 * Restores every Smart Calendar selection for one user + calendar bucket.
 *
 * A null key means the signed-in user is not known yet, which yields defaults and reads nothing —
 * there is deliberately no shared bucket to fall back to (VE-2605).
 *
 * The pre-VE-2259 global `renewalGroupView` key is also not consulted. VE-2259 read it once so a
 * saved view was not lost on upgrade, but being global it handed whatever that browser held to any
 * user without a Group View of their own — the same cross-user leak in a narrower form. Losing a
 * pre-release Group View once is the cheaper trade.
 * @param {string | null} storageKey - A key from {@link smartCalendarPrefsKey}, or null.
 * @returns {SmartCalendarPrefs} The saved selections, with defaults for anything unusable.
 */
export const readSmartCalendarPrefs = (storageKey: string | null): SmartCalendarPrefs => {
  if (!storageKey) return DEFAULT_SMART_CALENDAR_PREFS;

  const stored = parseStoredPrefs(storageKey);

  return {
    groupView: readOption(stored?.groupView, GROUP_VIEWS, DEFAULT_SMART_CALENDAR_PREFS.groupView),
    categories: readCategories(stored?.categories),
    clientContacts: readClientContacts(stored?.clientContacts),
    expiresIn: readOption(stored?.expiresIn, EXPIRES_IN_PRESETS, DEFAULT_SMART_CALENDAR_PREFS.expiresIn),
    status: readOption(stored?.status, CALENDAR_STATUSES, DEFAULT_SMART_CALENDAR_PREFS.status),
    renewalZone: readOption(stored?.renewalZone, RENEWAL_ZONES, DEFAULT_SMART_CALENDAR_PREFS.renewalZone)
  };
};

/**
 * Saves every Smart Calendar selection for one user + calendar bucket.
 *
 * Takes the complete set and replaces the stored object, which is what lets a cleared filter stay
 * cleared: a partial merge would leave the previous value in place for the field just emptied.
 * Best-effort — a calendar must keep working when storage is full or unavailable.
 *
 * A null key discards the write (VE-2605). A session that does not yet know who it belongs to has
 * no bucket of its own, and must not be given a shared one to write into.
 * @param {string | null} storageKey - A key from {@link smartCalendarPrefsKey}, or null.
 * @param {SmartCalendarPrefs} prefs - The complete current selections.
 * @returns {void}
 */
export const writeSmartCalendarPrefs = (storageKey: string | null, prefs: SmartCalendarPrefs): void => {
  if (!storageKey) return;

  try {
    localStorage.setItem(storageKey, JSON.stringify(prefs));
  } catch (_e) {
    // Storage unavailable or over quota. Losing a saved preference is not worth breaking the view.
  }
};

/**
 * Re-resolves the Expires In preset against today.
 *
 * `full` maps to undefined, which is how the renewals fetch asks for no expiry cutoff.
 * @param {ExpiresInPreset} preset - The stored preset.
 * @returns {Date | undefined} The cutoff date, or undefined for the full range.
 */
export const expiresInPresetToDate = (preset: ExpiresInPreset): Date | undefined =>
  preset === 'full' ? undefined : moment().add(1, 'year').toDate();
